import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Alert, Typography } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

interface PasswordResetPageProps {
  uid: string;
  token: string;
  onBackToLogin: () => void;
}

const PasswordResetPage: React.FC<PasswordResetPageProps> = ({ uid, token, onBackToLogin }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!uid || !token) {
      setError('Etibarsız şifrə bərpası linki');
    }
  }, [uid, token]);

  const handleSubmit = async (values: { new_password: string; confirm_password: string }) => {
    if (!uid || !token) {
      setError('Etibarsız şifrə bərpası linki');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await authAPI.confirmPasswordReset(
        uid,
        token,
        values.new_password,
        values.confirm_password
      );
      
      if (response.success) {
        setIsSuccess(true);
        // 3 saniyə sonra login səhifəsinə yönləndir
        setTimeout(() => {
          onBackToLogin();
        }, 3000);
      } else {
        setError(
          response.errors?.non_field_errors?.[0] ||
          response.errors?.new_password?.[0] ||
          response.message ||
          'Şifrə dəyişdirilmədi'
        );
      }
    } catch (error) {
      setError('Server ilə əlaqə xətası');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card 
          className="w-full max-w-md shadow-2xl text-center"
          style={{ borderRadius: '12px' }}
        >
          <div className="py-8">
            <CheckCircleOutlined className="text-6xl text-green-500 mb-4" />
            <Title level={3} className="text-green-600 mb-4">
              Şifrə Uğurla Dəyişdirildi!
            </Title>
            <Text type="secondary" className="text-base">
              Şifrəniz uğurla yeniləndi. İndi yeni şifrə ilə daxil ola bilərsiniz.
            </Text>
            <div className="mt-6">
              <Text type="secondary" className="text-sm">
                3 saniyə sonra login səhifəsinə yönləndiriləcəksiniz...
              </Text>
            </div>
            <div className="mt-4">
              <Button 
                type="primary" 
                onClick={onBackToLogin}
                className="bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700"
              >
                İndi Daxil Ol
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card 
        className="w-full max-w-md shadow-2xl"
        style={{ borderRadius: '12px' }}
      >
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 bg-indigo-600 rounded-full flex items-center justify-center mb-4">
            <LockOutlined className="text-2xl text-white" />
          </div>
          <Title level={2} className="mb-2">Yeni Şifrə Təyin Et</Title>
          <Text type="secondary">Yeni şifrənizi daxil edin</Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        <Form
          form={form}
          name="password-reset-confirm"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="new_password"
            label="Yeni şifrə"
            rules={[
              { required: true, message: 'Yeni şifrə tələb olunur!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Şifrəni təkrarla"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Şifrə təkrarı tələb olunur!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifrələr uyğun gəlmir!'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              size="large"
              className="bg-indigo-600 hover:bg-indigo-700 border-indigo-600 hover:border-indigo-700"
            >
              {isLoading ? 'Şifrə Dəyişdirilir...' : 'Şifrəni Dəyişdir'}
            </Button>
          </Form.Item>

          <Form.Item className="mb-0">
            <Button
              type="default"
              onClick={onBackToLogin}
              block
              size="large"
            >
              Geri Qayıt
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PasswordResetPage;